// Standard error codes https://developer.mozilla.org/en-US/docs/Web/HTTP/Status

import { getProp, setProp } from "../tools/any.tools";
import { anyReallyAny } from "../tools/object.tools";

/**
 * * Reserved error codes
 * 400 - 429 client route errors @cervval.js/client-routes
 * 500 - 529 server route errors @cervval.js/server-routes
 * 530 - 539 authentication errors @cervval.js/common-auth
 * 540 - 549 permissions errors @cervval.js/common-permissions
 * 580 - 599 sql errors @cervval.js/server-core and @cervval.js/server-sql-core
 * 600 - 899 application errors
 * 900 - 919 action errors @cervval.js/common-action
 * 920 - 929 exceljs errors 
 * 
 * 999 GENERIC_ERROR_CODE (unexpected error => standard error message)
 * 
 * 2000 - 2999 zurvan.js errors
 * 3000 - 3999 digitwin.js errors
 * 
 * RESERVED_ERROR_CODES should be added in all ErrorCode enumerations in order to have direct access to the comment above (like the one below)
 */
export const RESERVED_ERROR_CODES = 0;

export enum ErrorCodes {
    RESERVED = RESERVED_ERROR_CODES, // * You can check all reserved codes here

    GENERIC_ERROR_CODE = 999 // Code for error fallback message
}

export interface ErrorDebugInfos extends Record<string, unknown> {
    error: Error;
}

/**
 * Error infos that may be logged and/or displayed to user
 */
export interface ErrorWithCode {
    code: number;// Error code (can be http or application dependent, see Reserved codes on top of this file)
    errorMessage?: string;// The error message to display to the user (will be retrieved from ErrorMessageService if not provided)
    debugInfos?: Partial<ErrorDebugInfos>;// debugInfos is not sent to client when in production if error occurs on server side
    doNotLog?: boolean;// This is set to true if error is handled or if it was already logged
    noDialog?: boolean; // No dialog box will be displayed by client
}

/**
 * Wraps ErrorWithCode, Error or any in a ErrorWithCode object
 * Adds code and debugInfos if they are not already present in err
 * @returns a ErrorWithCode with debug infos
 */
export function wrapError<DebugInfos = Record<string, unknown>>(
    err: ErrorWithCode | Error | anyReallyAny, defaultValues?: { code?: number, debugInfos?: DebugInfos }): ErrorWithCode {
    let errorWithCode: ErrorWithCode | undefined = undefined;
    let error: Error | undefined = undefined;
    let errorString: string | undefined = undefined;
    const originalErr = err;// originalErr can be null or undefined
    err ??= {};
    const typeOfErr = typeof err;
    if (typeOfErr === "string") {
        // err is a string, we will encapsulate if in a ErrorWithCode
        errorString = String(err);
    } else if (typeOfErr === "object" && ("errorMessage" in err || "code" in err)
        && !("sql" in err)/* sql errors have a code but are not ErrorWithCode */
        && !("severity" in err)/* pgsql errors have a code but are not ErrorWithCode */) {
        // err is a ErrorWithCode, we keep it and eventually add missing parts
        errorWithCode = err;
        error = err.debugInfos?.error;
    } else if (typeOfErr === "object" && "stack" in err) {
        // err is an Error, we will encapsulate if in a ErrorWithCode
        error = err;
        if (err.message) {
            errorString = (err.name ? err.name + ": " : "") + err.message;
        }
    } else {
        // err not recognized => keep errorString=undefined, a standard error message will be displayed by client
        error = new Error(JSON.stringify(originalErr ?? null));// we keep err within error's message in debugInfos
    }
    // create a ErrorWithCode if err was not already a ErrorWithCode
    if (!errorWithCode) {
        errorWithCode = {
            code: defaultValues?.code ?? ErrorCodes.GENERIC_ERROR_CODE,
            errorMessage: errorString// The error message to display to the user
        };
    }
    // add debugInfos if missing
    if (!errorWithCode.debugInfos) { errorWithCode.debugInfos = { error: error ?? new Error(errorString) }; }
    // set other debugInfos if missing
    if (defaultValues?.debugInfos) {
        for (const debugInfoKey in defaultValues.debugInfos) {
            if (!(debugInfoKey in errorWithCode.debugInfos)) {
                errorWithCode.debugInfos[debugInfoKey] = defaultValues.debugInfos[debugInfoKey]!;
            }
        }
    }
    // set error code if missing
    if (errorWithCode.code === undefined && defaultValues?.code !== undefined) {
        errorWithCode.code = defaultValues.code;
    }
    return errorWithCode;
}


export const errorLogSettings = {
    maxLogInfoLength: 255
};

/**
 * Limit length of log infos
 */
export function truncateStringForLogIfNeeded(str?: string): string | undefined {
    if (!str) { return str; }
    if (str && str.length > errorLogSettings.maxLogInfoLength) {
        str = str.substring(0, errorLogSettings.maxLogInfoLength - 15) + "... truncated";
    }
    return str;
}

/**
 * Limit length of log infos within object (not recursive)
 * {str:"long string...", value: 2} => {str:"truncated string", value: 2}
 */
export function truncateStringValuesInObject(object: unknown): void {
    if (object && typeof object === "object") {
        for (const k in object) {
            const info = getProp(object, k);
            if (typeof info === "string") {
                setProp(object, k, truncateStringForLogIfNeeded(info));
            }
        }
    }
}
